import React, { useEffect, useState } from "react";
import FormInputText from "../common/FormInputText";
import Button from "../common/button/Button"
import ButtonType from "../common/button/ButtonType"
import ButtonAction from "../common/button/ButtonAction"
import Annotation from "../../model/Annotation";
import { useAlerts } from "../../contexts/AlertsContext";
import DialogWindowType from "../common/dialog_window/DialogWindowType";
import { useApiClient } from "../../contexts/ApiClientContext";
import APIEndPoints from "../../utils/APIEndPoints";
import PagedData from "../../model/PagedData";

interface AlterationEvent {
  id: number;
  name: string;
}

interface Props {
  formInputChange: (inputID: string, newValue: string) => void;
  activeTab: string;
  hint?: string;
  selectedAnnotations: Annotation[];
  annotations: Annotation[];
  onClickAnnotationCard: (id: number | undefined) => void;
}

const ConditionReportAddEdit_annotations: React.FC<Props> = ({
  formInputChange,
  activeTab,
  hint,
  selectedAnnotations,
  annotations,
  onClickAnnotationCard,
}) => {

  // reference to the ApiClient instance
  const apiClient = useApiClient();

  // State for managing the input value
  const [inputValue, setInputValue] = useState<string>("");
  // Define the set of Alterations corresponding to the set of Annotations
  const [alterations, setAlterations] = useState<Map<number, string>>(new Map<number, string>());


  useEffect(() => {

      filterSearchAnnotations(inputValue.toLowerCase());

  }, [apiClient, annotations]);

  // Load the Alteration (name) corresponding to each Annotation, since
  // Annotation itself carries no usable name for this view.
  useEffect(() => {
    if (!apiClient || annotations.length === 0) {
      return;
    }

    let cancelled = false;

    Promise.all(
      annotations
        .filter((annotation) => annotation.id !== undefined)
        .map((annotation) =>
          apiClient
            .get<PagedData<AlterationEvent[]>>({
              url: APIEndPoints.ANNOTATIONS.ALTERATION_EVENTS(annotation.id!),
              requiresToken: true,
            })
            .then((response) => [annotation.id!, response?.items?.[0]?.name] as const)
            .catch(() => [annotation.id!, undefined] as const)
        )
    ).then((results) => {
      if (cancelled) {
        return;
      }
      // console.log("==========================")
      // console.log(results)
      setAlterations(
        new Map(
          results.filter(
            (entry): entry is [number, string] => entry[1] !== undefined
          )
        )
      );
    });

    return () => {
      cancelled = true;
    };
  }, [apiClient, annotations]);


  const alertsManager = useAlerts();

  const [showMore, setShowMore] = useState(false);
  const [displayedAnnotations, setDisplayedAnnotation] = useState<Annotation[]>([]);

  // Add this function to handle annotation removal
  const removeSelectedAnnotation = (id: number) => {
    onClickAnnotationCard(id);
  };

    const handleShowMore = () => {
      setShowMore(false);
      setDisplayedAnnotation(annotations);
    };

  const filterSearchAnnotations = (filterString: string = "") => {
    // console.log("Annotations received:", annotations)

    // let filterString = inputValue.toLowerCase();
    const filtered = annotations.filter((a) =>
       a.description?.toLowerCase().includes(filterString)
    );
    // console.log("Filer:", filterString, ":")
    // console.log("Annotations filtered:", filtered)
    // setDisplayedAnnotation(filtered);
    if (filtered.length > 4) {
        setShowMore(true);
        setDisplayedAnnotation(filtered.slice(0, 4));
    } else {
        setShowMore(false);
        setDisplayedAnnotation(filtered);
    }
  }

  const handleInputChange = (id: string, value: string) => {
    setInputValue(value);
    filterSearchAnnotations(value);
  };

  return (
    <>
    {/* <hr className="my-4 border-t-2 border-gray-300" />
    <h4>Annotations</h4> */}
     <label className="form-label">Alterations:</label>
    {hint && <div className="form-hint">{hint}</div>}

     <div className="form-input-group-vertical">
          {/* Annotation Selection */} 
          <div>
            <FormInputText
              label="Filter Annotations by keyword:"
              id="name"
              value={inputValue}
              placeholder="Annotation Name"
              onChange={handleInputChange}
            />
          </div>
          {/* <p className="text-sm text-gray-600 mt-2">You typed: {inputValue}</p> */}

          {/* Associated Annotations */}
          <div
            className="details-section"
            key={activeTab + "-" + selectedAnnotations.join("-")}
          >
            {/* <h4>Matching Annotations:</h4> */}
            {annotations.length === 0 && <p>No Annotations to select from...</p>}
            {annotations.length > 0 &&
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4 m-4">
                    {displayedAnnotations.map((annotation) => (
                      <div
                        key={annotation.id}
                        id={`annotation-${annotation.id}`}
                        className={`bg-white shadow rounded-lg p-4 cursor-pointer transition-colors duration-200 ${
                          selectedAnnotations.some(a => a.id === annotation.id)
                            ? "bg-green-100 !important"
                            : "bg-white"
                        }`}
                        style={{
                          backgroundColor: selectedAnnotations.some(a => a.id === annotation.id)
                            ? "#d4edda"
                            : "white",
                        }}
                        onClick={() => onClickAnnotationCard(annotation.id)}
                        onMouseOver={() =>
                          console.log(
                            `Hovering over annotation ${annotation.id}, class:`,
                            document.getElementById(`annotation-${annotation.id}`)
                              ?.className
                          )
                        }
                      >
                        <h3 className="text-sm font-semibold mb-2">
                          {alterations.get(annotation.id!) ?? annotation.name}
                        </h3>
                        <p className="text-xs text-gray-500">
                          {/* Display only th first 15 characters of the description */}
                          Description: {annotation.description.length>10 ? annotation.description.slice(0, 15)+"...":annotation.description}.
                        </p>

                      </div>
                    ))}

                  {showMore && (
                  <div className="details-show-more">
                    <Button
                      label="Show all Annotations"
                      onClick={handleShowMore}
                      purpose={ButtonAction.UPDATE}
                    />
                  </div>
                )}
              </div>
            }
          </div>

          <h4>Selected Annotations:</h4>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
          {selectedAnnotations &&
            selectedAnnotations.length > 0 &&
            selectedAnnotations.map((annotation) => {
            return (
              <div className="details-grid-element" key={annotation.id}>
              <div className="details-grid-content p-0 flex flex-row">
                <div className="details-grid-description grow">
                 <h3 className="text-sm font-semibold mb-2">
                          {alterations.get(annotation.id!) ?? annotation.name}
                        </h3>
                        <p className="text-xs text-gray-500">
                          {/* Display only th first 15 characters of the description */}
                          Description: {annotation.description.length>10 ? annotation.description.slice(0, 15)+"...":annotation.description}.   
                        </p>
                </div>
                <Button
                  type={ButtonType.ICON}
                  purpose={ButtonAction.DELETE}
                  label=""
                  onClick={() => {
                    // alertsManager.showDialogWindow({
                    // type: DialogWindowType.CONFIRM,
                    // message:
                    //   "Are you sure you want to remove this user from the list?",
                    // cancelLabel: "Cancel",
                    // confirmLabel: "Confirm",
                    // onConfirm: () => removeSelectedAnnotation(a),
                    // });
                    removeSelectedAnnotation(annotation.id!)
                  }}
                />
              </div>
              </div>
            );
            })}
          </div>
     
      </div>
    </>
  );
};

export default ConditionReportAddEdit_annotations;
